import NestedAccordion from "../../../components/NestedAccordion";
import {
  FileDetails,
  RawFootageVersions,
  RecordingStageAdminRequiredActions,
  TAdditionalFiles,
  TRawFootage,
} from "@/types/projectType";
import FileUploadField from "@/components/ui/FileUploadField";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { getTimeDifference } from "@/utils/getTimeDifference";
import {
  useUploadAdditionalFileMutation,
  useUploadRawFootageMutation,
  useUploadRevisedRawFootageMutation,
} from "@/redux/feature/orderManagement/RawFootage/rawFootageApi";
import { FileCard } from "@/pages/orderMananagement/components/FileCard";
import { RevisionFileCard } from "@/pages/orderMananagement/components/RevisionFileCard";
import Modal from "@/components/ui/Modal";
import WarningIcon from "@/assets/icons/DisclaimerGreenIcon.svg";
import { CustomCheckbox } from "@/components/ui/Checkbox";
import { CustomButton } from "@/components/ui/Buttons";
import HaveMoreToShow from "../VideoSetup/components/HaveMoreToShow";
import { handleApiRequest } from "@/utils/handleApiRequest";
import { Download, FileIcon, Link2 } from "lucide-react";
import { formatFileSize } from "@/utils/formatFileSize";
import { handleDownload } from "@/utils/handleDownload";
import { useGetDriveLinkQuery } from "@/redux/feature/driveLink/driveLinkApi";
import { useGlobalContext } from "@/context/GlobalContext";

const RawFootage = ({
  rawFootageData,
  ActionsRequired,
  projectId,
}: {
  ActionsRequired?: RecordingStageAdminRequiredActions[];
  rawFootageData: TRawFootage;
  projectId: string;
}) => {
  // Derived flag: should the accordion open because action is required?
  const isActionRequired = ActionsRequired?.find(
    (item) =>
      item?.isRawFootageUploaded === false ||
      item.isRevisedRawFootageUploaded === false,
  );

  const [searchParams] = useSearchParams();
  const { id: orderId } = useParams();
  const { userData } = useGlobalContext();
  const videoId = searchParams.get("videoId") || undefined;
  // Local UI state
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [note, setNote] = useState("");
  const { data } = useGetDriveLinkQuery({
    email: userData?.email,
    projectId,
    videoId: videoId as string,
    type: "raw_footage",
  });
  const driveLink = data?.data?.drive_link;

  // File input state
  const [files, setFiles] = useState<FileDetails[]>([]);
  const [clearFiles, setClearFiles] = useState(false); // NOTE: legacy prop needed by HaveMoreToShow. Consider replacing with key-remount pattern.
  const [resetKey, setResetKey] = useState(0); // use to force-remount FileUploadField after successful uploads

  // Raw footage flags
  const { inProgress = false, isApproved = false } =
    rawFootageData?.status || {};
  const { requiresRevision = false, requiresApproval = false } =
    rawFootageData?.requiredActions || {};

  // RTK mutations with loading states to prevent double submits
  const [uploadRawFootage, { isLoading: isUploadingRaw }] =
    useUploadRawFootageMutation();
  const [uploadNewRawFootage, { isLoading: isUploadingRevised }] =
    useUploadRevisedRawFootageMutation();
  const [uploadAdditionalFile, { isLoading: isAdditionalFIleUploading }] =
    useUploadAdditionalFileMutation();
  const isUploading = isUploadingRaw || isUploadingRevised;

  // Compute timeline text once
  const timeLine = useMemo(
    () => getTimeDifference(rawFootageData?.adminTimeLine),
    [rawFootageData?.adminTimeLine],
  );

  // Current and previous versions: compute once, avoid repeated slice/reverse in render
  const currentVersion = useMemo(() => {
    const list = rawFootageData?.rawFootageVersions ?? [];
    return list[list.length - 1];
  }, [rawFootageData?.rawFootageVersions]);

  const showAdditionalFileUploadField = useMemo(
    () => !isApproved && !requiresRevision && requiresApproval,
    [isApproved, requiresRevision, requiresApproval],
  );

  const showAllVersion = useMemo(
    () => !isApproved && !requiresRevision,
    [isApproved, requiresRevision],
  );

  const previousVersions = useMemo(() => {
    const list = rawFootageData?.rawFootageVersions ?? [];
    const end = showAllVersion ? list.length : Math.max(0, list.length - 1);
    // slice once, reverse once
    return list.slice(0, end).reverse();
  }, [rawFootageData?.rawFootageVersions, showAllVersion]);

  // Display status: compute once
  const status = useMemo(() => {
    if (requiresRevision) return "Needs Revision";
    if (requiresApproval) return "Not Approved Yet";
    if (inProgress) return "In Progress";
    if (isApproved) return "Approved";
    return "";
  }, [requiresRevision, requiresApproval, inProgress, isApproved]);

  // Auto-open when action is required; keep behavior stable
  useEffect(() => {
    const shouldOpen =
      isActionRequired?.isRawFootageUploaded === false ||
      isActionRequired?.isRevisedRawFootageUploaded === false;
    setIsOpen(Boolean(shouldOpen));
  }, [
    isActionRequired?.isRawFootageUploaded,
    isActionRequired?.isRevisedRawFootageUploaded,
  ]);

  // if the file is uploaded already then the disclaimer pop up will not show agin
  useEffect(() => {
    if (!isApproved && requiresApproval) {
      console.log("called");
      setDisclaimerAccepted(true);
    }
  }, [requiresApproval, isApproved]);

  // Clear-files flag reset after any successful clear (keeps one render-frame pulse)
  useEffect(() => {
    if (clearFiles) {
      const t = setTimeout(() => setClearFiles(false), 0);
      return () => clearTimeout(t);
    }
  }, [clearFiles]);

  // Upload handler: guarded, single responsibility, prevents double-submit
  const handleUploadRawFootage = useCallback(async () => {
    if (!orderId || !videoId) {
      toast.error("Missing order/video context");
      return;
    }
    // if (files.length === 0 || isUploading) return;

    try {
      if (requiresRevision) {
        // When revision is requested, send the revised file payload

        const res = await uploadNewRawFootage({
          orderId,
          videoId,
          data: {
            description: "Revised Footage From Admin",
            newRawFootageFile: files[0],
            isUploadingWithDriveLink: files.length === 0,
            driveLink: driveLink,
          },
        }).unwrap();

        if (res?.success) {
          toast.success("Revised raw footage uploaded successfully!");
          setFiles([]);
          setClearFiles(true); // legacy prop for external components
          setResetKey((k) => k + 1); // re-mount the input to visually reset
          return;
        }
      } else {
        // First-time or subsequent non-revision uploads
        const versionNumber =
          (rawFootageData?.rawFootageVersions?.length ?? 0) + 1;
        const res = await uploadRawFootage({
          orderId: orderId,
          videoId: videoId,
          data: {
            versionNumber,
            uploadedBy: "creator",
            description:
              versionNumber === 1
                ? "First version of raw footage"
                : `Version ${versionNumber}`,
            file: files,
            isUploadingWithDriveLink: files.length === 0,
            driveLink: driveLink,
          },
        }).unwrap();

        if (res?.success) {
          toast.success("Raw footage uploaded successfully!");
          setFiles([]);
          setClearFiles(true); // legacy prop for external components
          setResetKey((k) => k + 1); // re-mount the input to visually reset
          return;
        }
      }

      // Fallback if no success flag returned
      toast.error("Upload failed");
    } catch (err: any) {
      // Prefer server message; otherwise, show generic error
      toast.error(err?.data?.message || "Failed to upload raw footage");
      console.error(err);
    }
  }, [
    files,
    isUploading,
    orderId,
    videoId,
    requiresRevision,
    rawFootageData?.rawFootageVersions?.length,
    uploadNewRawFootage,
    uploadRawFootage,
  ]);

  const handleAdditionalFile = useCallback(async () => {
    if (!orderId || !videoId) {
      toast.error("Missing order/video context");
      return;
    }

    if (isApproved && !requiresApproval) {
      toast.error("Additional File can not upload");
      return;
    }

    if (files.length === 0 || isAdditionalFIleUploading) return;
    console.log("calling this api");
    const trimmedNote = note.trim();
    const payload = {
      orderId,
      videoId,
      data: {
        description: "Additional File",
        files: files[0], // optional when using drive link
        // driveLink: "",
        // isUploadingWithDriveLink: false,
        uploadedBy: "creator", // defaults to admin
        versionNumber: currentVersion?.versionNumber, // defaults to latest version
        note: trimmedNote,
        isUploadingWithDriveLink: files.length === 0,
        driveLink: driveLink,
      },
    };

    await handleApiRequest({
      requestFunction: uploadAdditionalFile,
      payload: payload,
      onSuccess: (res) => {
        toast.success("Revised raw footage uploaded successfully!");
        setFiles([]);
        setClearFiles(true); // legacy prop for external components
        setResetKey((k) => k + 1); // re-mount the input to visually reset
        setNote("");
        return;
      },
      onError: (err) => {
        console.log(err);
      },
    });
  }, [
    files,
    note, // <-- include note so the latest value is captured
    isApproved, // <-- used in guard
    requiresApproval, // <-- used in guard
    isAdditionalFIleUploading, // <-- fix typo at source
    orderId,
    videoId,
    currentVersion?.versionNumber,
    uploadAdditionalFile,
  ]);

  return (
    <NestedAccordion
      title="Raw Footage"
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      actionRequired={
        isActionRequired?.isRawFootageUploaded === false ||
        isActionRequired?.isRevisedRawFootageUploaded === false
      }
      isApproved={isApproved}
      initiated={!!rawFootageData}
      timeLine={timeLine}
      status={status}
    >
      <div className="p-3 relative">
        {/* If user already uploaded something and we're awaiting approval (no revision yet),
           show the "Have more to show" addendum uploader. */}
        {showAdditionalFileUploadField && (
          <HaveMoreToShow
            comments={note}
            setComments={setNote}
            clearFiles={clearFiles}
            setSetupFiles={setFiles}
          />
        )}

        {/* Main upload area (blocked until disclaimer accepted when approval not yet granted) */}
        {!isApproved && !requiresApproval && (
          <fieldset
            disabled={!disclaimerAccepted}
            aria-describedby="uploadHelp"
          >
            {/* Overlay that blocks clicks before disclaimer is accepted */}
            {!disclaimerAccepted && (
              <div
                className="absolute inset-0 z-40 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowDisclaimer(true);
                }}
              />
            )}
            <FileUploadField
              key={resetKey}
              clearFiles={clearFiles}
              onFilesUploaded={setFiles}
              maxFiles={1}
            />
            {!disclaimerAccepted && (
              <p id="uploadHelp" className="mt-2 text-xs text-gray-600">
                Please confirm the quality checklist before uploading.
              </p>
            )}
            {/* Google Drive Upload Option */}
            <p className="text-center py-4"> or</p>

            <div className="w-full rounded-lg border bg-gray-50 flex flex-col items-center justify-center py-6 p-4">
              <p className="text-gray-700 text-sm mb-2">
                Upload your file to Google Drive
              </p>

              <Link
                to={driveLink}
                target="_blank"
                className="bg-blue-500 hover:text-white font-medium text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition"
              >
                Upload to Google Drive
              </Link>
            </div>
          </fieldset>
        )}

        {/* Final approved version */}
        {isApproved && currentVersion && (
          <>
            <p className="text-[13px] font-medium mt-4">Final Version</p>

            <div>
              <VersionCard
                item={currentVersion}
                isApproved={isApproved}
                additionalFiles={currentVersion.additionalFiles}
              />
            </div>
          </>
        )}

        {/* Current revision + comment, if revision was requested */}
        {requiresRevision && currentVersion && (
          <>
            <p className="text-[13px] font-medium mt-4">
              Request Revisions (
              {rawFootageData?.rawFootageVersions?.length ?? 0})
            </p>
            <RevisionFileCard
              active
              file={currentVersion.file?.[0]}
              comment={currentVersion.revisionComment as string}
            />
          </>
        )}

        {/* Previous versions list (computed once) */}
        {previousVersions.length > 0 && (
          <div>
            <p className="text-[13px] font-medium mt-4">Previous Version</p>
            <div>
              {previousVersions.map((item, i) => (
                <VersionCard
                  item={item}
                  key={i}
                  additionalFiles={item?.additionalFiles}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer action row */}
      <div className="bg-[#F5F9FF] p-3 rounded-bl-xl rounded-br-xl flex justify-between items-center">
        <p className="text-sm text-[#344054] font-medium">Cancel</p>
        <button
          type="button"
          onClick={
            !isApproved && requiresApproval
              ? handleAdditionalFile
              : handleUploadRawFootage
          }
          disabled={
            // files.length === 0 ||
            !orderId ||
            !videoId ||
            !disclaimerAccepted ||
            isUploading ||
            isAdditionalFIleUploading
          }
          className="text-white disabled:opacity-50 text-sm px-3 py-2 font-semibold rounded-lg bg-[#17457D]"
        >
          {isUploading ? "Uploading…" : "Submit upload"}
        </button>
      </div>

      {/* Disclaimer modal: must be accepted before the uploader is enabled */}
      <Modal width="640px" isOpen={showDisclaimer} setOpen={setShowDisclaimer}>
        <DisclaimerModal
          setShowDisclaimer={setShowDisclaimer}
          setDisclaimerAccepted={setDisclaimerAccepted}
        />
      </Modal>

      {/* Trigger disclaimer when user attempts to interact before accepting */}
      {!disclaimerAccepted && !isApproved && !requiresApproval && (
        <div className="sr-only" aria-live="polite">
          Please confirm the disclaimer to enable uploading.
        </div>
      )}
    </NestedAccordion>
  );
};

export default RawFootage;
type DisclaimerModalProps = {
  setShowDisclaimer: (val: boolean) => void;
  setDisclaimerAccepted: (val: boolean) => void;
};

const DisclaimerModal = ({
  setShowDisclaimer,
  setDisclaimerAccepted,
}: DisclaimerModalProps) => {
  const [isAgreed, setAgreed] = useState(false);

  const handleConfirm = () => {
    if (!isAgreed) return;
    setDisclaimerAccepted(true);
    setShowDisclaimer(false);
  };

  const instructions = [
    {
      label: "Clear Audio",
      desc: "No noise or muffled sound. Use an external mic.",
    },
    {
      label: "Good Lighting",
      desc: "Well-lit, no shadows on face or background. Side/natural light preferred.",
    },
    {
      label: "Proper Framing",
      desc: "Stay centered, stable, with space around you.",
    },
    {
      label: "High Resolution",
      desc: "1080p required.",
    },
    {
      label: "No Visual Distractions",
      desc: "No clutter, no visual distractions.",
    },
    {
      label: "Natural Delivery",
      desc: "Don’t sound like you're reading. Speak clearly, confidently, and avoid heavy edits.",
    },
    {
      label: "Eye Contact",
      desc: "Look into the camera, not off-screen.",
    },
    {
      label: "Consistent Attire & Setting",
      desc: "Match attire and setup if part of a series.",
    },
    {
      label: "Presentable Appearance",
      desc: "Ironed clothes, groomed look if dressing formally.",
    },
    {
      label: "Lighting Tip",
      desc: "Avoid ring light reflections in glasses/eyes.",
    },
    {
      label: "Green Screen",
      desc: "No green elements; maintain strong contrast.",
    },
    {
      label: "Final Check",
      desc: "Review footage for blurs or quality issues before uploading.",
    },
  ];
  return (
    <div className="p-4">
      <div className="flex items-center gap-x-4">
        <WarningIcon />{" "}
        <div>
          <h2 className="text-[16px] font-semibold text-[#101828]">
            Before Uploading Your Raw Footage
          </h2>
          <p className="text-[#475467] text-[12px]">
            Please ensure the following quality checks are met
          </p>
        </div>
      </div>

      <ul className="list-disc pl-6 py-[32px] space-y-[20px] ">
        {instructions.map((item, i) => (
          <li>
            <p className="text-[14px] ">
              <span className="font-semibold text-[#222] ">{item.label}</span> -{" "}
              <span className="text-[#475467] "> {item.desc}</span>
            </p>
          </li>
        ))}
      </ul>

      <div className="flex items-center gap-x-4">
        <CustomCheckbox
          checked={isAgreed}
          onChange={() => setAgreed(!isAgreed)}
        />
        <p> I confirm that my footage meets the above standards.</p>
      </div>
      <div className="pt-4">
        <CustomButton
          disabled={!isAgreed}
          onClick={handleConfirm}
          backgroundColor="#17457D"
          className="w-full"
          color="#ffffff"
        >
          Confirm
        </CustomButton>
      </div>
    </div>
  );
};

const VersionCard = ({
  item: MainVersion,
  additionalFiles,
  isApproved = false,
}: {
  item: RawFootageVersions;
  isApproved?: boolean;

  additionalFiles: TAdditionalFiles[];
}) => {
  return (
    <div className="border rounded-[12px] px-2 py-1">
      <FileCard
        requiresRevision={false}
        active={isApproved}
        isApproved={isApproved}
        file={MainVersion?.file?.[0]}
      />

      <div className="pt-2 border-t space-y-2">
        {additionalFiles?.map((item, i) => (
          <div
            key={i}
            className="p-2 md:p-4 border-[1.5px] border-[#F0EEEE] w-full rounded-xl"
          >
            <div className="flex justify-between items-center">
              <div className="flex gap-2 items-start">
                <Link2 />
                <div>
                  <div className="flex items-center gap-x-2">
                    <h6 className="text-sm  md:w-fit w-[150px] truncate leading-5 text-black font-medium">
                      {typeof item === "string"
                        ? "Drive Link"
                        : item.files[0].name}{" "}
                    </h6>
                    <span className="border rounded-full text-xs px-3 font-medium py-1">
                      {" "}
                      Attached Submission
                    </span>
                  </div>
                  <p className="text-xs leading-[15px] text-[#959FB2] mt-1">
                    {typeof item === "string" ? (
                      <>
                        <Link target="_blank" to={item}>
                          View Link
                        </Link>
                      </>
                    ) : (
                      <> {formatFileSize(item?.files[0]?.size)}</>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-x-4">
                {/* Download Button */}
                <div className="flex gap-x-4">
                  {typeof item !== "string" && (
                    <div
                      onClick={() =>
                        handleDownload(
                          item?.files[0].live_url as string,
                          item?.files[0]?.name,
                        )
                      }
                      className="text-caption-large font-semibold text-gray-800 cursor-pointer"
                    >
                      <Download className="h-5" />
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Comment */}
            {item?.note && (
              <div className="mt-4 px-2">
                <p className="text-[13px] mb-3 text-[#475467] font-medium">
                  Comment{" "}
                </p>

                <p className="bg-[#F7F7F7] p-[12px] border text-[#667085]   rounded-[10px] border-[#F1F1F1]">
                  {item?.note}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
